import React, { useState } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useNavigate } from "react-router-dom";
import {
  faGear,
  faUser,
  faCog,
  // faBasketShopping, // Service Catalog menu entry temporarily hidden
  faCircleQuestion,
  faComments,
  faRightFromBracket,
  faChevronLeft,
  faBars,
  faBuilding,
  faArrowRightArrowLeft,
} from "@fortawesome/pro-light-svg-icons";
import {
  useV2WhoAmIQuery,
  useV2GetAccountQuery,
  useV2ListAccountsQuery,
} from "@generated";
import SwitchAccountModal, { ACCOUNT_LIST_ARGS } from "./SwitchAccountModal";
import {
  isSupportChatAvailable,
  openSupportChat,
} from "../../utils/supportChat";

interface UserMenuCardProps {
  expanded: boolean;
  onToggle: () => void;
  /** When true (inside a chrome root that carries the /users route),
   *  the User Profile link navigates client-side instead of full-GET. */
  clientSideNav?: boolean;
}

const menuLinks = [
  {
    label: "User Profile",
    href: "/users/edit",
    icon: faUser,
    id: "user-profile",
  },
  {
    label: "Settings",
    href: "/admin/sections",
    icon: faCog,
    id: "account-settings",
  },
  // Service Catalog temporarily hidden from the user menu.
  // {
  //   label: "Service Catalog",
  //   href: "/catalog_items",
  //   icon: faBasketShopping,
  //   id: "service-catalog",
  // },
  {
    label: "Help Center",
    href: "https://support.scopestack.io/help",
    icon: faCircleQuestion,
    id: "help-center",
    external: true,
  },
  // Mirrors the classic navbar's `ask-support` item: an inert href whose
  // click hands off to the HubSpot widget. Dropped from the menu when the
  // layout did not load HubSpot -- see utils/supportChat.
  {
    label: "Ask Support",
    href: "#",
    icon: faComments,
    id: "ask-support",
  },
  {
    label: "Sign Out",
    href: "/users/sign_out",
    icon: faRightFromBracket,
    id: "signout",
    method: "delete" as const,
  },
];

/** Rendered first on its own; the account section follows it. */
const profileLink = menuLinks.filter((link) => link.id === "user-profile");
const remainingLinks = menuLinks.filter((link) => link.id !== "user-profile");

const Divider: React.FC = () => (
  <div
    data-testid="menu-divider"
    style={{
      borderTop: "1px solid rgba(255,255,255,0.1)",
      margin: "8px 12px",
    }}
  />
);

const linkStyle: React.CSSProperties = {
  color: "rgba(255,255,255,0.7)",
  textDecoration: "none",
  padding: "8px 12px",
  display: "flex",
  alignItems: "center",
  gap: 10,
  fontSize: 13,
  fontFamily: "Open Sans, sans-serif",
  borderRadius: 4,
  transition: "background-color 0.15s ease",
};

/** The account the user is currently signed in to. Informational only. */
const AccountRow: React.FC<{ expanded: boolean; accountName: string }> = ({
  expanded,
  accountName,
}) => {
  const row = (
    <div
      id="current-account"
      style={{
        ...linkStyle,
        color: "#63C4AB",
        justifyContent: expanded ? "flex-start" : "center",
      }}
    >
      <FontAwesomeIcon
        icon={faBuilding}
        style={{ width: 16, fontSize: 14, flexShrink: 0 }}
      />
      {expanded && (
        <span
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {accountName}
        </span>
      )}
    </div>
  );

  if (expanded) return row;

  return (
    <OverlayTrigger
      placement="right"
      overlay={<Tooltip id="tooltip-current-account">{accountName}</Tooltip>}
    >
      {row}
    </OverlayTrigger>
  );
};

const SwitchAccountRow: React.FC<{
  expanded: boolean;
  onClick: () => void;
}> = ({ expanded, onClick }) => {
  const button = (
    <button
      id="switch-account"
      onClick={onClick}
      style={{
        ...linkStyle,
        background: "none",
        border: "none",
        width: "100%",
        justifyContent: expanded ? "flex-start" : "center",
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLElement).style.backgroundColor =
          "rgba(255,255,255,0.05)";
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLElement).style.backgroundColor = "transparent";
      }}
    >
      <FontAwesomeIcon
        icon={faArrowRightArrowLeft}
        style={{ width: 16, fontSize: 14, flexShrink: 0 }}
      />
      {expanded && <span>Switch Account</span>}
    </button>
  );

  if (expanded) return button;

  return (
    <OverlayTrigger
      placement="right"
      overlay={<Tooltip id="tooltip-switch-account">Switch Account</Tooltip>}
    >
      {button}
    </OverlayTrigger>
  );
};

const UserMenuCard: React.FC<UserMenuCardProps> = ({
  expanded,
  onToggle,
  clientSideNav = false,
}) => {
  const [open, setOpen] = useState(false);
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const navigate = useNavigate();

  // One /v2/me and one /v2/account, shared with LaunchDarklyProvider's cache
  // entries; this used to fetch /v1/me twice through two different hooks.
  const { data: whoAmI } = useV2WhoAmIQuery();
  const currentUser = whoAmI?.data;
  const isSiteAdmin = currentUser?.attributes?.["site-admin"] === true;
  const accountSlug = currentUser?.attributes?.["account-slug"];
  const { data: account } = useV2GetAccountQuery({});
  const accountName = account?.data?.attributes?.name;

  // Site admins can always switch. Everyone else only when they belong to
  // more than one account — counted from the same query (and cache entry)
  // the modal uses, so opening the switcher costs no extra request.
  const { data: accountList } = useV2ListAccountsQuery(ACCOUNT_LIST_ARGS, {
    skip: !open || isSiteAdmin,
  });
  const canSwitchAccounts = isSiteAdmin || (accountList?.data?.length ?? 0) > 1;
  const hasAccountSection = Boolean(accountName) || canSwitchAccounts;

  // Read on every render rather than at module scope: the HubSpot snippet is
  // an inline <script> in the Rails layout, so this is already true by the
  // time react_ujs mounts on DOMContentLoaded -- but only where the layout
  // rendered it at all (`in_the_wild?`).
  const visibleRemainingLinks = isSupportChatAvailable()
    ? remainingLinks
    : remainingLinks.filter((link) => link.id !== "ask-support");

  const userName = currentUser?.attributes?.name || "";
  const meUserId = currentUser?.id;
  const profileHref = meUserId
    ? `/users/${meUserId}/edit/contact`
    : "/users/edit";

  const handleSignOut = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const form = document.createElement("form");
    form.method = "POST";
    form.action = "/users/sign_out";
    const methodInput = document.createElement("input");
    methodInput.type = "hidden";
    methodInput.name = "_method";
    methodInput.value = "delete";
    form.appendChild(methodInput);
    const csrfToken = document
      .querySelector('meta[name="csrf-token"]')
      ?.getAttribute("content");
    if (csrfToken) {
      const csrfInput = document.createElement("input");
      csrfInput.type = "hidden";
      csrfInput.name = "authenticity_token";
      csrfInput.value = csrfToken;
      form.appendChild(csrfInput);
    }
    document.body.appendChild(form);
    form.submit();
  };

  const handleAskSupport = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    openSupportChat();
    setOpen(false);
  };

  const renderLinks = (links: typeof menuLinks) =>
    links.map((link) => {
      const isSignOut = link.id === "signout";
      const isProfile = link.id === "user-profile";
      const href = isProfile ? profileHref : link.href;
      const handleClick = isSignOut
        ? handleSignOut
        : link.id === "ask-support"
        ? handleAskSupport
        : isProfile && clientSideNav && meUserId
        ? (e: React.MouseEvent<HTMLAnchorElement>) => {
            if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0)
              return;
            e.preventDefault();
            navigate(profileHref);
            setOpen(false);
          }
        : undefined;

      const anchor = (
        <a
          key={link.id}
          href={href}
          id={link.id}
          target={link.external ? "_blank" : undefined}
          rel={link.external ? "noopener noreferrer" : undefined}
          onClick={handleClick}
          style={{
            ...linkStyle,
            justifyContent: expanded ? "flex-start" : "center",
          }}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLElement).style.backgroundColor =
              "rgba(255,255,255,0.05)";
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLElement).style.backgroundColor =
              "transparent";
          }}
        >
          <FontAwesomeIcon
            icon={link.icon}
            style={{ width: 16, fontSize: 14, flexShrink: 0 }}
          />
          {expanded && <span>{link.label}</span>}
        </a>
      );

      if (expanded) return anchor;

      return (
        <OverlayTrigger
          key={link.id}
          placement="right"
          overlay={<Tooltip id={`tooltip-${link.id}`}>{link.label}</Tooltip>}
        >
          {anchor}
        </OverlayTrigger>
      );
    });

  return (
    <div
      style={{
        borderTop: "1px solid rgba(255,255,255,0.1)",
        marginTop: "auto",
      }}
    >
      {/* Card body */}
      {open && (
        <div style={{ padding: "8px 4px" }}>
          {renderLinks(profileLink)}
          <Divider />
          {accountName && (
            <AccountRow expanded={expanded} accountName={accountName} />
          )}
          {canSwitchAccounts && (
            <SwitchAccountRow
              expanded={expanded}
              onClick={() => setSwitcherOpen(true)}
            />
          )}
          {hasAccountSection && <Divider />}
          {renderLinks(visibleRemainingLinks)}
        </div>
      )}

      {/* Card header */}
      <div
        style={{
          padding: expanded ? "12px 16px" : "12px 0",
          display: "flex",
          alignItems: "center",
          justifyContent: expanded ? "space-between" : "center",
          gap: 8,
        }}
      >
        {expanded && (
          <span
            style={{
              color: "rgba(255,255,255,0.7)",
              fontSize: 13,
              fontWeight: 700,
              fontFamily: "Open Sans, sans-serif",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              flex: 1,
            }}
          >
            {userName}
          </span>
        )}
        <button
          onClick={() => setOpen((prev) => !prev)}
          aria-label="User menu"
          style={{
            background: "none",
            border: "none",
            color: open ? "#63C4AB" : "rgba(255,255,255,0.5)",
            cursor: "pointer",
            padding: 6,
            borderRadius: 4,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <FontAwesomeIcon
            icon={faGear}
            style={{ fontSize: expanded ? 16 : 20 }}
          />
        </button>
      </div>

      {/* Card footer — collapse toggle */}
      <div
        style={{
          borderTop: "1px solid rgba(255,255,255,0.1)",
          padding: "12px 0",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <button
          onClick={onToggle}
          aria-label={expanded ? "Collapse sidebar" : "Expand sidebar"}
          style={{
            background: "none",
            border: "none",
            color: "rgba(255,255,255,0.5)",
            cursor: "pointer",
            padding: "6px 12px",
            borderRadius: 4,
            display: "flex",
            alignItems: "center",
            gap: 8,
            fontSize: 14,
            fontFamily: "Open Sans, sans-serif",
          }}
        >
          <FontAwesomeIcon
            icon={expanded ? faChevronLeft : faBars}
            style={{ fontSize: expanded ? 14 : 18 }}
          />
          {expanded && (
            <span style={{ color: "rgba(255,255,255,0.5)" }}>Collapse</span>
          )}
        </button>
      </div>

      <SwitchAccountModal
        show={switcherOpen}
        onClose={() => setSwitcherOpen(false)}
        currentAccountSlug={accountSlug}
      />
    </div>
  );
};

export default UserMenuCard;
